import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import { Button } from '../ui/Button';
import TextInput from '../ui/TextInput';
import { Label } from '../ui/Label';
import { cn } from '../../lib/utils';
import type { MenuItem } from './Sidebar';
import {
  SidebarLayout,
  SIDEBAR_TOP_LEVEL_KEY,
  createEmptyLayout,
  flattenSidebarLeaves,
  getItemKey,
  getSectionKey,
  newCustomSectionId,
  saveSidebarLayout,
} from '../../utils/sidebarLayout';

interface SidebarCustomizerProps {
  /** When true, render inline (no modal). */
  embedded?: boolean;
  /** Required for modal mode; ignored when embedded (always active). */
  isOpen?: boolean;
  onClose?: () => void;
  /** Permission-filtered menu (before layout apply). */
  baseItems: MenuItem[];
  initialLayout: SidebarLayout | null;
}

type EditorSection = {
  key: string;
  name: string;
  isCustom: boolean;
  /** Top-level links (no group), like Dashboard. */
  isTopLevel?: boolean;
  itemKeys: string[];
};

function buildEditorSections(baseItems: MenuItem[], layout: SidebarLayout | null): EditorSection[] {
  const structural = baseItems.filter((i) => i.name !== 'Dashboard' && i.name !== 'Favorites');
  const leafByKey = new Map(
    flattenSidebarLeaves(structural).map((leaf) => [getItemKey(leaf), leaf] as const)
  );
  const working = layout ?? createEmptyLayout(baseItems);

  const assigned = new Set<string>();
  for (const k of working.topLevelItemKeys || []) assigned.add(k);
  for (const sec of working.customSections) {
    for (const k of sec.itemKeys) assigned.add(k);
  }

  const topLevelSection: EditorSection = {
    key: SIDEBAR_TOP_LEVEL_KEY,
    name: 'No group',
    isCustom: false,
    isTopLevel: true,
    itemKeys: (working.topLevelItemKeys || []).filter((k) => leafByKey.has(k)),
  };

  const defaultSections: EditorSection[] = structural.map((section) => {
    const leaves = flattenSidebarLeaves([section])
      .map(getItemKey)
      .filter((k) => leafByKey.has(k) && !assigned.has(k));
    return {
      key: getSectionKey(section),
      name: section.name,
      isCustom: false,
      itemKeys: leaves,
    };
  });

  const customSections: EditorSection[] = working.customSections.map((sec) => ({
    key: sec.id,
    name: sec.name,
    isCustom: true,
    itemKeys: sec.itemKeys.filter((k) => leafByKey.has(k)),
  }));

  const byKey = new Map<string, EditorSection>();
  for (const s of [...defaultSections, ...customSections]) byKey.set(s.key, s);

  const order =
    working.sectionOrder.length > 0
      ? working.sectionOrder
      : [...defaultSections.map((s) => s.key), ...customSections.map((s) => s.key)];

  const ordered: EditorSection[] = [];
  const used = new Set<string>();
  for (const key of order) {
    if (key === SIDEBAR_TOP_LEVEL_KEY) continue;
    const s = byKey.get(key);
    if (s && !used.has(key)) {
      ordered.push(s);
      used.add(key);
    }
  }
  for (const s of Array.from(byKey.values())) {
    if (!used.has(s.key)) ordered.push(s);
  }
  return [topLevelSection, ...ordered];
}

function toLayout(sections: EditorSection[]): SidebarLayout {
  const topLevel = sections.find((s) => s.isTopLevel);
  const groups = sections.filter((s) => !s.isTopLevel);
  return {
    version: 1,
    topLevelItemKeys: topLevel?.itemKeys ?? [],
    sectionOrder: groups.map((s) => s.key),
    customSections: groups
      .filter((s) => s.isCustom)
      .map((s) => ({
        id: s.key,
        name: s.name.trim() || 'Custom',
        itemKeys: s.itemKeys,
      })),
  };
}

const SidebarCustomizer: React.FC<SidebarCustomizerProps> = ({
  embedded = false,
  isOpen = false,
  onClose,
  baseItems,
  initialLayout,
}) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [sections, setSections] = useState<EditorSection[]>([]);
  const [saving, setSaving] = useState(false);
  const [dragSectionIndex, setDragSectionIndex] = useState<number | null>(null);
  const [dragOverSectionIndex, setDragOverSectionIndex] = useState<number | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  /** Section keys that are expanded (accordion). */
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());
  const active = embedded || isOpen;

  const isSectionExpanded = (key: string) => expandedKeys.has(key);

  const toggleSectionExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const leafLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const leaf of flattenSidebarLeaves(baseItems)) {
      map.set(getItemKey(leaf), leaf.name);
    }
    return map;
  }, [baseItems]);

  const unassignedKeys = useMemo(() => {
    const all = flattenSidebarLeaves(baseItems).map(getItemKey);
    const used = new Set(sections.flatMap((s) => s.itemKeys));
    return all.filter((k) => !used.has(k));
  }, [baseItems, sections]);

  useEffect(() => {
    if (!active) return;
    const nextSections = buildEditorSections(baseItems, initialLayout);
    setSections(nextSections);
    // Start collapsed so groups act as accordion; keep any already-expanded keys that still exist.
    setExpandedKeys((prev) => {
      const next = new Set<string>();
      for (const s of nextSections) {
        if (prev.has(s.key)) next.add(s.key);
      }
      return next;
    });
    setNewGroupName('');
  }, [active, baseItems, initialLayout]);

  const moveSection = (from: number, to: number) => {
    setSections((prev) => {
      // Keep "No group" pinned at index 0
      if (from === 0 || to === 0) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      if (item.isTopLevel) return prev;
      next.splice(to, 0, item);
      return next;
    });
  };

  const renameSection = (key: string, name: string) => {
    setSections((prev) => prev.map((s) => (s.key === key && s.isCustom ? { ...s, name } : s)));
  };

  const removeCustomSection = (key: string) => {
    setSections((prev) => {
      const target = prev.find((s) => s.key === key);
      if (!target?.isCustom) return prev;
      const without = prev.filter((s) => s.key !== key);
      if (target.itemKeys.length === 0) return without;
      const firstGroup = without.find((s) => !s.isTopLevel && !s.isCustom) ?? without.find((s) => !s.isTopLevel);
      if (!firstGroup) return without;
      return without.map((s) =>
        s.key === firstGroup.key
          ? { ...s, itemKeys: [...s.itemKeys, ...target.itemKeys] }
          : s
      );
    });
  };

  const addCustomSection = () => {
    const name = newGroupName.trim() || 'My group';
    const id = newCustomSectionId();
    setSections((prev) => [...prev, { key: id, name, isCustom: true, itemKeys: [] }]);
    setExpandedKeys((prev) => new Set(prev).add(id));
    setNewGroupName('');
  };

  const moveItemToSection = (itemKey: string, toSectionKey: string) => {
    setSections((prev) =>
      prev.map((s) => {
        const without = s.itemKeys.filter((k) => k !== itemKey);
        if (s.key === toSectionKey) {
          return { ...s, itemKeys: without.includes(itemKey) ? without : [...without, itemKey] };
        }
        return { ...s, itemKeys: without };
      })
    );
  };

  const moveItemWithinSection = (sectionKey: string, from: number, to: number) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== sectionKey) return s;
        const keys = [...s.itemKeys];
        const [item] = keys.splice(from, 1);
        keys.splice(to, 0, item);
        return { ...s, itemKeys: keys };
      })
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const layout = toLayout(sections);
      await saveSidebarLayout(layout);
      showSuccess('Sidebar layout saved');
      if (!embedded) onClose?.();
    } catch (e) {
      console.error(e);
      showError('Failed to save sidebar layout');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      await saveSidebarLayout(null);
      setSections(buildEditorSections(baseItems, null));
      showSuccess('Sidebar reset to default');
      if (!embedded) onClose?.();
    } catch (e) {
      console.error(e);
      showError('Failed to reset sidebar');
    } finally {
      setSaving(false);
    }
  };

  const onSectionDragStart = useCallback((index: number) => setDragSectionIndex(index), []);
  const onSectionDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverSectionIndex(index);
  }, []);
  const onSectionDrop = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (
        dragSectionIndex === null ||
        dragSectionIndex === index ||
        dragSectionIndex === 0 ||
        index === 0
      ) {
        setDragSectionIndex(null);
        setDragOverSectionIndex(null);
        return;
      }
      moveSection(dragSectionIndex, index);
      setDragSectionIndex(null);
      setDragOverSectionIndex(null);
    },
    [dragSectionIndex]
  );

  const editorBody = (
    <div className="flex flex-col gap-4">
      <p className={cn('text-sm', colors.textSecondary)}>
        Expand a group to manage its modules. Move items into <span className="font-medium">No group</span> to show
        them as top-level links like Dashboard. Drag groups to reorder. Dashboard and Favorites stay at the top.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[160px] flex-1">
          <Label className="mb-1.5">New group name</Label>
          <TextInput
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="e.g. Daily work"
          />
        </div>
        <Button type="button" variant="secondary" onClick={addCustomSection}>
          Add group
        </Button>
      </div>

      <div className="space-y-3">
        {sections.map((section, index) => {
          const expanded = isSectionExpanded(section.key);
          const canReorderGroup = !section.isTopLevel;
          return (
          <div
            key={section.key}
            draggable={canReorderGroup}
            onDragStart={() => {
              if (!canReorderGroup) return;
              onSectionDragStart(index);
            }}
            onDragOver={(e) => {
              if (!canReorderGroup) return;
              onSectionDragOver(e, index);
            }}
            onDragLeave={() => setDragOverSectionIndex(null)}
            onDrop={(e) => {
              if (!canReorderGroup) return;
              onSectionDrop(e, index);
            }}
            onDragEnd={() => {
              setDragSectionIndex(null);
              setDragOverSectionIndex(null);
            }}
            className={cn(
              'rounded-xl border overflow-hidden transition-colors',
              colors.border,
              colors.surface,
              section.isTopLevel && 'border-dashed',
              dragOverSectionIndex === index && 'ring-2 ring-teal-500/60',
              dragSectionIndex === index && 'opacity-60'
            )}
          >
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-2.5 cursor-pointer',
                'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'
              )}
              onClick={() => toggleSectionExpanded(section.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSectionExpanded(section.key);
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
            >
              {canReorderGroup ? (
                <span
                  className={cn('cursor-grab select-none text-xs', colors.textMuted)}
                  title="Drag to reorder"
                  onClick={(e) => e.stopPropagation()}
                >
                  ⋮⋮
                </span>
              ) : (
                <span className="w-3" aria-hidden />
              )}
              <svg
                className={cn(
                  'h-4 w-4 shrink-0 transition-transform',
                  colors.textMuted,
                  expanded && 'rotate-180'
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {section.isCustom ? (
                <input
                  value={section.name}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => renameSection(section.key, e.target.value)}
                  className={cn(
                    'min-w-0 flex-1 rounded-md border px-2 py-1 text-sm font-medium',
                    colors.border,
                    colors.surface,
                    colors.text
                  )}
                />
              ) : (
                <div className="min-w-0 flex-1">
                  <span className={cn('block truncate text-sm font-semibold', colors.text)}>
                    {section.name}
                  </span>
                  {section.isTopLevel && (
                    <span className={cn('block truncate text-[11px]', colors.textMuted)}>
                      Top-level links, same style as Dashboard
                    </span>
                  )}
                </div>
              )}
              <span className={cn('shrink-0 text-xs tabular-nums', colors.textMuted)}>
                {section.itemKeys.length}
              </span>
              {section.isCustom && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeCustomSection(section.key);
                  }}
                >
                  Remove
                </Button>
              )}
            </div>

            {expanded && (
            <ul className={cn('space-y-1 border-t px-3 pb-3 pt-2', colors.border)}>
              {section.itemKeys.length === 0 && (
                <li className={cn('px-2 py-1 text-xs', colors.textMuted)}>
                  {section.isTopLevel
                    ? 'Move modules here to show them without a group'
                    : 'No modules in this group'}
                </li>
              )}
              {section.itemKeys.map((itemKey, itemIndex) => (
                <li
                  key={itemKey}
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.setData(
                      'application/x-sidebar-item',
                      JSON.stringify({ itemKey, fromSection: section.key, fromIndex: itemIndex })
                    );
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      const raw = e.dataTransfer.getData('application/x-sidebar-item');
                      if (!raw) return;
                      const payload = JSON.parse(raw) as {
                        itemKey: string;
                        fromSection: string;
                        fromIndex: number;
                      };
                      if (payload.fromSection === section.key) {
                        moveItemWithinSection(section.key, payload.fromIndex, itemIndex);
                      } else {
                        moveItemToSection(payload.itemKey, section.key);
                      }
                    } catch {
                      // ignore
                    }
                  }}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm',
                    colors.borderLight,
                    colors.surfaceHover
                  )}
                >
                  <span className={cn('cursor-grab text-xs', colors.textMuted)}>⋮⋮</span>
                  <span className={cn('flex-1 truncate', colors.text)}>{leafLabels.get(itemKey) || itemKey}</span>
                  <select
                    aria-label="Move to group"
                    className={cn(
                      'max-w-[140px] rounded border px-1.5 py-1 text-xs',
                      colors.border,
                      colors.surface,
                      colors.text
                    )}
                    value={section.key}
                    onChange={(e) => moveItemToSection(itemKey, e.target.value)}
                  >
                    {sections.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.isTopLevel ? 'No group (top-level)' : s.name}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
              <li
                className="h-2"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const raw = e.dataTransfer.getData('application/x-sidebar-item');
                    if (!raw) return;
                    const payload = JSON.parse(raw) as { itemKey: string; fromSection: string };
                    if (payload.fromSection !== section.key) {
                      moveItemToSection(payload.itemKey, section.key);
                    }
                  } catch {
                    // ignore
                  }
                }}
              />
            </ul>
            )}
          </div>
          );
        })}

        {unassignedKeys.length > 0 && (
          <div className={cn('rounded-xl border border-dashed p-3', colors.border)}>
            <p className={cn('mb-2 text-xs font-medium', colors.textMuted)}>Unassigned</p>
            <ul className="space-y-1">
              {unassignedKeys.map((itemKey) => (
                <li key={itemKey} className={cn('flex items-center gap-2 text-sm', colors.text)}>
                  <span className="flex-1">{leafLabels.get(itemKey) || itemKey}</span>
                  <select
                    className={cn('rounded border px-1.5 py-1 text-xs', colors.border, colors.surface, colors.text)}
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) moveItemToSection(itemKey, e.target.value);
                    }}
                  >
                    <option value="">Move to…</option>
                    {sections.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.isTopLevel ? 'No group (top-level)' : s.name}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  const actions = (
    <div className={cn('flex flex-wrap items-center justify-end gap-2', embedded && 'border-t pt-4', embedded && colors.border)}>
      {!embedded && (
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      )}
      <Button variant="secondary" onClick={handleReset} disabled={saving}>
        Reset to default
      </Button>
      <Button onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save layout'}
      </Button>
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-5">
        {editorBody}
        {actions}
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose ?? (() => undefined)} size="lg">
      <ModalHeader>
        <ModalTitle>Customize sidebar</ModalTitle>
      </ModalHeader>
      <ModalContent>{editorBody}</ModalContent>
      <ModalFooter>{actions}</ModalFooter>
    </Modal>
  );
};

export default SidebarCustomizer;
